import React, { useState, useEffect } from 'react';

const EmailManagementPanel = ({ adminToken }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('providers');
  const [activeProvider, setActiveProvider] = useState('resend');
  const [providers, setProviders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testTemplate, setTestTemplate] = useState('welcome');
  const [sending, setSending] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [savingTestEmail, setSavingTestEmail] = useState(false);
  
  // Bulk email states
  const [bulkAudience, setBulkAudience] = useState('all'); // 'all' or 'paid'
  const [bulkTemplate, setBulkTemplate] = useState('discount_coupon');
  const [bulkVariables, setBulkVariables] = useState({});
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkStats, setBulkStats] = useState({ total: 0, sent: 0, failed: 0 });
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    template_type: '',
    subject: '',
    html_body: '',
    text_body: '',
    variables: []
  });
  
  const [providerConfig, setProviderConfig] = useState({
    name: 'resend',
    display_name: 'Resend',
    is_active: true,
    api_key: '',
    config: {}
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  useEffect(() => {
    if (adminToken) {
      loadProviders();
      loadNotifications();
      loadEmailLogs();
      loadTemplates();
      loadTestEmailAddress();
    }
  }, [adminToken]);

  const loadTestEmailAddress = async () => {
    console.log('[TEST EMAIL] Loading from settings...');
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[TEST EMAIL] Loaded value:', data.testEmailAddress || '(empty)');
        setTestEmailAddress(data.testEmailAddress || '');
      }
    } catch (error) {
      console.error('[TEST EMAIL] Error loading:', error);
    }
  };

  const saveTestEmailAddress = async () => {
    console.log('[TEST EMAIL] Saving:', testEmailAddress);
    setSavingTestEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/set-test-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testEmailAddress })
      });

      console.log('[TEST EMAIL] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[TEST EMAIL] Response data:', data);
        showToast(data.message, 'success');
        
        // Reload from database to confirm it was saved
        console.log('[TEST EMAIL] Reloading from database to confirm...');
        setTimeout(() => {
          loadTestEmailAddress();
        }, 500);
      } else {
        const errorData = await response.json();
        console.error('[TEST EMAIL] Error response:', errorData);
        showToast('Failed to save test email', 'error');
      }
    } catch (error) {
      console.error('[TEST EMAIL] Exception:', error);
      showToast('Error saving test email', 'error');
    } finally {
      setSavingTestEmail(false);
    }
  };
  
  // Load templates when Templates tab is opened
  useEffect(() => {
    if (activeTab === 'templates' && adminToken && templates.length === 0) {
      loadTemplates();
    }
  }, [activeTab]);

  const loadProviders = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/providers`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
        if (data.providers.length > 0) {
          setProviderConfig(data.providers[0]);
        }
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/notifications`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const loadEmailLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading email logs:', error);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      console.log('[Templates] Loading from:', `${BACKEND_URL}/api/admin/email/templates`);
      console.log('[Templates] Using token:', adminToken ? 'Yes' : 'No');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/email/templates`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      console.log('[Templates] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Templates] Loaded:', data.templates?.length || 0, 'templates');
        console.log('[Templates] Data:', data);
        setTemplates(data.templates || []);
        if (!data.templates || data.templates.length === 0) {
          showToast('⚠️ No templates found in database', 'error');
        }
      } else {
        const errorText = await response.text();
        console.error('[Templates] Failed:', response.status, errorText);
        showToast(`Failed to load templates: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('[Templates] Error:', error);
      showToast(`Error loading templates: ${error.message}`, 'error');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_type: template.template_type,
      subject: template.subject,
      html_body: template.html_body,
      text_body: template.text_body,
      variables: template.variables || []
    });
  };

  const saveTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/templates/${editingTemplate.template_type}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        showToast('✅ Template saved successfully!', 'success');
        setEditingTemplate(null);
        loadTemplates();
      } else {
        showToast('❌ Error saving template', 'error');
      }
    } catch (error) {
      showToast('❌ Error saving template', 'error');
    }
  };

  const cancelEditTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      template_type: '',
      subject: '',
      html_body: '',
      text_body: '',
      variables: []
    });
  };

  const deleteTemplate = (template) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/templates/${templateToDelete.template_type}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        showToast('🗑️ Template deleted', 'success');
        loadTemplates();
        setTemplateToDelete(null);
      } else {
        showToast('❌ Error deleting template', 'error');
      }
    } catch (e) { showToast('❌ Error deleting template', 'error'); }
    setDeleting(false);
  };

  const sanitizeAllTemplatesCss = async () => {
    if (!window.confirm('Sanitize CSS for all templates? This swaps gradient backgrounds for white, white text for black, and forces buttons to white-bg + black-text + black border so they show in any email client. Recommended.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/templates/sanitize-css`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        showToast(`🎨 Sanitized ${data.templates_updated}/${data.templates_total} templates`, 'success');
        loadTemplates();
      } else {
        showToast('❌ Error sanitizing templates', 'error');
      }
    } catch (e) { showToast('❌ Error sanitizing templates', 'error'); }
  };

  const previewTemplate = (template) => {
    // Create sample data for variables
    const sampleData = {};
    (template.variables || []).forEach(v => {
      // Provide sample values based on variable name
      if (v.includes('name')) sampleData[v] = 'John Doe';
      else if (v.includes('email')) sampleData[v] = 'user@example.com';
      else if (v.includes('link') || v.includes('url')) sampleData[v] = 'https://example.com';
      else if (v.includes('date')) sampleData[v] = new Date().toLocaleDateString();
      else if (v.includes('time')) sampleData[v] = new Date().toLocaleTimeString();
      else if (v.includes('amount') || v.includes('price')) sampleData[v] = '$99.00';
      else if (v.includes('percent') || v.includes('discount')) sampleData[v] = '50';
      else if (v.includes('code') || v.includes('coupon')) sampleData[v] = 'SAMPLE50';
      else if (v.includes('ip')) sampleData[v] = '192.168.1.1';
      else if (v.includes('device')) sampleData[v] = 'Chrome on Windows';
      else if (v.includes('location')) sampleData[v] = 'New York, USA';
      else sampleData[v] = `[${v}]`; // Default placeholder
    });
    
    // Replace variables in HTML
    let previewHtml = template.html_body;
    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewHtml = previewHtml.replace(regex, sampleData[key]);
    });
    
    // Open preview in new window
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    if (previewWindow) {
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
    } else {
      showToast('⚠️ Please allow popups to preview templates', 'error');
    }
  };

  const sendTestToAdmin = async (template) => {
    setSelectedTemplate(template);
    setShowSendModal(true);
  };

  const confirmSendTestToAdmin = async () => {
    setShowSendModal(false);
    
    if (!selectedTemplate) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/test-to-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_type: selectedTemplate.template_type
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        showToast(`✅ Test email sent to ${data.sent_to}!`, 'success');
      } else {
        const error = await response.json();
        showToast(`❌ Error: ${error.detail || 'Failed to send'}`, 'error');
      }
    } catch (error) {
      showToast('❌ Error sending test email', 'error');
    } finally {
      setSelectedTemplate(null);
    }
  };

  const saveProvider = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/providers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(providerConfig)
      });
      
      if (response.ok) {
        showToast('✅ Email provider saved successfully!', 'success');
        loadProviders();
      } else {
        showToast('❌ Error saving provider', 'error');
      }
    } catch (error) {
      showToast('❌ Error saving provider', 'error');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      showToast('Please enter email address', 'error');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: providerConfig.name,
          api_key: providerConfig.api_key,
          to_email: testEmail,
          template_type: testTemplate
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast('✅ Test email sent successfully!', 'success');
        setTestEmail('');
        loadEmailLogs();
      } else {
        showToast(`❌ Error: ${data.error || 'Failed to send'}`, 'error');
      }
    } catch (error) {
      showToast('❌ Error sending test email', 'error');
    } finally {
      setSending(false);
    }
  };

  const sendBulkEmail = async () => {
    if (!bulkTemplate) {
      showToast('Please select a template', 'error');
      return;
    }
    
    // Confirm before sending
    const audienceText = bulkAudience === 'all' ? 'ALL users' : 'PAID members only';
    if (!window.confirm(`⚠️ Are you sure you want to send this email to ${audienceText}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setSendingBulk(true);
    setBulkStats({ total: 0, sent: 0, failed: 0 });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          audience: bulkAudience,
          template_type: bulkTemplate,
          variables: bulkVariables
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBulkStats({
          total: data.total || 0,
          sent: data.sent || 0,
          failed: data.failed || 0
        });
        showToast(`✅ Bulk email sent! ${data.sent}/${data.total} delivered`, 'success');
        loadEmailLogs();
      } else {
        showToast(`❌ Error: ${data.error || 'Failed to send'}`, 'error');
      }
    } catch (error) {
      showToast('❌ Error sending bulk email', 'error');
    } finally {
      setSendingBulk(false);
    }
  };

  const providerTabs = [
    { id: 'resend', name: 'Resend', icon: '📧', color: 'bg-purple-600' },
    { id: 'smtp', name: 'SMTP (Plesk)', icon: '📮', color: 'bg-orange-600' },
    { id: 'sendgrid', name: 'SendGrid', icon: '📨', color: 'bg-blue-600', disabled: true },
    { id: 'brevo', name: 'Brevo', icon: '✉️', color: 'bg-green-600', disabled: true }
  ];

  // SMTP Configuration state
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '587',
    user: '',
    password: '',
    from_email: '',
    from_name: 'DarijaPro',
    use_tls: true,
    use_ssl: false
  });

  const tabs = [
    { id: 'providers', label: 'Email Providers', icon: '📧' },
    { id: 'templates', label: 'Templates', icon: '📝' },
    { id: 'bulk', label: 'Bulk Emails', icon: '📬' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'logs', label: 'Email Logs', icon: '📊' },
    { id: 'test', label: 'Send Test', icon: '🧪' }
  ];

  if (loading) {
    return <div className="p-6 text-center">Loading email settings...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold dark:text-white">📧 Email Management</h2>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-6">
        <div className="w-64 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-lg mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          {/* Email Providers Tab */}
          {activeTab === 'providers' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">Email Service Providers</h3>
              
              {/* Provider Tabs */}
              <div className="flex gap-2 mb-6">
                {providerTabs.map(provider => (
                  <button
                    key={provider.id}
                    onClick={() => !provider.disabled && setActiveProvider(provider.id)}
                    disabled={provider.disabled}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      activeProvider === provider.id
                        ? `${provider.color} text-white`
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    } ${provider.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                  >
                    <span className="mr-2">{provider.icon}</span>
                    {provider.name}
                    {provider.disabled && ' (Coming Soon)'}
                  </button>
                ))}
              </div>

              {/* Resend Configuration */}
              {activeProvider === 'resend' && (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <h4 className="font-bold text-purple-900 dark:text-purple-100 mb-2">📧 Resend Configuration</h4>
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      Free tier: 3,000 emails/month • No credit card required
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      API Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={providerConfig.api_key}
                      onChange={(e) => setProviderConfig({...providerConfig, api_key: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="re_xxxxxxxxxxxx"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">resend.com/api-keys</a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Sender Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={providerConfig.config?.from_email || ''}
                      onChange={(e) => setProviderConfig({
                        ...providerConfig, 
                        config: {...(providerConfig.config || {}), from_email: e.target.value}
                      })}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="noreply@yourdomain.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use your verified domain (e.g., <span className="font-mono">noreply@yourdomain.com</span> or <span className="font-mono">Darija Learning &lt;noreply@yourdomain.com&gt;</span>)
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={providerConfig.is_active}
                        onChange={(e) => setProviderConfig({...providerConfig, is_active: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm dark:text-white">Active (Enable email sending)</span>
                    </label>
                  </div>

                  <button
                    onClick={saveProvider}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium"
                  >
                    💾 Save Configuration
                  </button>
                </div>
              )}

              {/* SMTP Configuration (Plesk) */}
              {activeProvider === 'smtp' && (
                <div className="space-y-4">
                  {/* Instructions */}
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h4 className="font-bold text-orange-900 dark:text-orange-100 mb-2">📮 SMTP Configuration (Plesk Email Server)</h4>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                      Use your Plesk email server to send emails. No external service required!
                    </p>
                    <div className="text-xs text-orange-600 dark:text-orange-400 space-y-1">
                      <p><strong>How to find your Plesk SMTP settings:</strong></p>
                      <ol className="list-decimal ml-4 space-y-1">
                        <li>Log into Plesk Panel</li>
                        <li>Go to Mail → Mail Accounts</li>
                        <li>Create or select an email account (e.g., noreply@yourdomain.com)</li>
                        <li>Click "Manual Configuration" to see SMTP settings</li>
                      </ol>
                    </div>
                  </div>

                  {/* Common Settings Info */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      <strong>Common Plesk Settings:</strong> Host: mail.yourdomain.com | Port: 587 (TLS) or 465 (SSL)
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        SMTP Host <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig({...smtpConfig, host: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="mail.yourdomain.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        Port <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={smtpConfig.port}
                        onChange={(e) => {
                          const port = e.target.value;
                          setSmtpConfig({
                            ...smtpConfig,
                            port,
                            use_tls: port === '587',
                            use_ssl: port === '465'
                          });
                        }}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="587">587 (TLS - Recommended)</option>
                        <option value="465">465 (SSL)</option>
                        <option value="25">25 (No encryption)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={smtpConfig.user}
                        onChange={(e) => setSmtpConfig({...smtpConfig, user: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="noreply@yourdomain.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">Usually your full email address</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={smtpConfig.password}
                        onChange={(e) => setSmtpConfig({...smtpConfig, password: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Your email password"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        From Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={smtpConfig.from_email}
                        onChange={(e) => setSmtpConfig({...smtpConfig, from_email: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="noreply@yourdomain.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        From Name
                      </label>
                      <input
                        type="text"
                        value={smtpConfig.from_name}
                        onChange={(e) => setSmtpConfig({...smtpConfig, from_name: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="DarijaPro"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={smtpConfig.use_tls}
                        onChange={(e) => setSmtpConfig({...smtpConfig, use_tls: e.target.checked, use_ssl: e.target.checked ? false : smtpConfig.use_ssl})}
                        className="rounded"
                      />
                      <span className="text-sm dark:text-white">Use TLS</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={smtpConfig.use_ssl}
                        onChange={(e) => setSmtpConfig({...smtpConfig, use_ssl: e.target.checked, use_tls: e.target.checked ? false : smtpConfig.use_tls})}
                        className="rounded"
                      />
                      <span className="text-sm dark:text-white">Use SSL</span>
                    </label>
                  </div>

                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/admin/email/providers`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                          body: JSON.stringify({
                            name: 'smtp',
                            api_key: smtpConfig.password,
                            is_active: true,
                            config: smtpConfig
                          })
                        });
                        if (res.ok) {
                          showToast('SMTP configuration saved!', 'success');
                        } else {
                          showToast('Failed to save SMTP configuration', 'error');
                        }
                      } catch (err) {
                        showToast('Error saving configuration', 'error');
                      }
                    }}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 font-medium"
                  >
                    💾 Save SMTP Configuration
                  </button>

                  {/* Test SMTP Connection */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <h5 className="font-bold text-green-900 dark:text-green-100 mb-2">🧪 Test SMTP Connection</h5>
                    <p className="text-xs text-green-700 dark:text-green-300 mb-3">
                      Send a test email to verify your SMTP settings are correct.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="test@example.com"
                        className="flex-1 px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        id="smtp-test-email"
                      />
                      <button
                        onClick={async () => {
                          const testEmail = document.getElementById('smtp-test-email').value;
                          if (!testEmail) {
                            showToast('Enter a test email address', 'error');
                            return;
                          }
                          try {
                            const res = await fetch(`${API}/admin/email/test`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                              body: JSON.stringify({
                                provider: 'smtp',
                                to_email: testEmail,
                                smtp_config: smtpConfig
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              showToast('Test email sent successfully!', 'success');
                            } else {
                              showToast(`Failed: ${data.error || 'Unknown error'}`, 'error');
                            }
                          } catch (err) {
                            showToast('Error sending test email', 'error');
                          }
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium text-sm"
                      >
                        Send Test
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Test Email Configuration */}
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🧪</span>
                  <div>
                    <h4 className="font-bold text-blue-900 dark:text-blue-100 text-lg">Test Email Configuration</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Set where "Send to Admin" test emails should go</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold dark:text-white mb-2">
                      Test Email Address
                    </label>
                    <input
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => {
                        console.log('[TEST EMAIL] Field changed to:', e.target.value);
                        setTestEmailAddress(e.target.value);
                      }}
                      className="w-full px-4 py-2 rounded-lg border-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="your-email@example.com"
                    />
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      💡 Leave empty to use your admin email. This is where "Send Test Email to Admin" button will send emails.
                    </p>
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                      <p className="text-xs text-yellow-800 dark:text-yellow-300 font-semibold">
                        ⚠️ Resend Free Tier Limitation:
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                        You can only send test emails to the email address you registered with on Resend. To send to other emails, verify a domain at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline">resend.com/domains</a>
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={saveTestEmailAddress}
                    disabled={savingTestEmail}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 font-semibold disabled:opacity-50 shadow-lg"
                  >
                    {savingTestEmail ? '💾 Saving...' : '💾 Save Test Email Address'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold dark:text-white">📝 Email Templates ({templates.length})</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingTemplate({ template_type: 'new_template' });
                      setTemplateForm({
                        template_type: '',
                        name: '',
                        description: '',
                        subject: '',
                        html_body: '',
                        text_body: '',
                        variables: [],
                        category: 'general',
                        is_active: true
                      });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
                  >
                    ➕ New Template
                  </button>
                  <button
                    onClick={sanitizeAllTemplatesCss}
                    className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 text-sm font-medium"
                    title="Make all templates email-client-safe (white backgrounds, black text, white buttons)"
                  >
                    🎨 Sanitize CSS
                  </button>
                  <button
                    onClick={loadTemplates}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    🔄 Reload
                  </button>
                </div>
              </div>
              
              {!editingTemplate ? (
                // Templates List
                <div className="space-y-3">
                  {templatesLoading ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-3"></div>
                      <div>Loading templates...</div>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-500 dark:text-gray-400 mb-4">No templates found</div>
                      <button
                        onClick={loadTemplates}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        🔄 Reload Templates
                      </button>
                    </div>
                  ) : (
                    templates.map(template => {
                      // Define colors and icons based on category
                      const categoryStyles = {
                        security: { color: 'from-purple-500 to-indigo-600', icon: '🔐' },
                        onboarding: { color: 'from-green-500 to-emerald-600', icon: '🎉' },
                        billing: { color: 'from-blue-500 to-cyan-600', icon: '💳' },
                        marketing: { color: 'from-pink-500 to-rose-600', icon: '🎁' },
                        general: { color: 'from-gray-500 to-slate-600', icon: '📧' }
                      };
                      
                      const style = categoryStyles[template.category] || categoryStyles.general;
                      
                      return (
                      <div key={template.template_type} className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-400 hover:shadow-lg transition-all">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${style.color} flex items-center justify-center text-2xl shadow-md`}>
                                {style.icon}
                              </div>
                              <div>
                                <div className="font-bold dark:text-white text-lg">
                                  {template.name || template.template_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Untitled Template'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Template Type: {template.template_type} • Category: {template.category || 'general'}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 ml-15">{template.description || 'No description available'}</div>
                            <div className="flex flex-wrap gap-1.5 ml-15">
                              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Variables:</span>
                              {(template.variables || []).map(v => (
                                <span key={v} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded font-mono">
                                  {`{{${v}}}`}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => editTemplate(template)}
                              className={`px-5 py-2.5 bg-gradient-to-r ${style.color} text-white rounded-lg hover:opacity-90 font-semibold shadow-md transition-all whitespace-nowrap`}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => previewTemplate(template)}
                              className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold shadow-md transition-all whitespace-nowrap"
                            >
                              👁️ Preview
                            </button>
                            <button
                              onClick={() => sendTestToAdmin(template)}
                              className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold shadow-md transition-all whitespace-nowrap"
                            >
                              📧 Send to Admin
                            </button>
                            <button
                              onClick={() => deleteTemplate(template)}
                              className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-md transition-all whitespace-nowrap"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                  )}
                </div>
              ) : (
                // Template Editor
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-bold dark:text-white">
                      Editing: {editingTemplate.template_type.replace('_', ' ').toUpperCase()}
                    </h4>
                    <button
                      onClick={cancelEditTemplate}
                      className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      ← Back to Templates
                    </button>
                  </div>

                  {/* Template Type (for new templates) */}
                  {editingTemplate.template_type === 'new_template' && (
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-2">
                        Template Type (ID) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={templateForm.template_type}
                        onChange={(e) => setTemplateForm({...templateForm, template_type: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="e.g., custom_welcome, special_offer"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Unique ID for this template (lowercase, use underscores)
                      </p>
                    </div>
                  )}
                  
                  {/* Template Name */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Template Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="e.g., Welcome Email - Pro"
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Brief description of this template"
                    />
                  </div>
                  
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Category
                    </label>
                    <select
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="general">General</option>
                      <option value="security">Security</option>
                      <option value="onboarding">Onboarding</option>
                      <option value="billing">Billing</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>
                  
                  {/* Variables Guide */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h5 className="font-bold text-blue-900 dark:text-blue-100 mb-2">📋 Template Variables</h5>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                      Add variable names (comma-separated) that will be used in this template:
                    </p>
                    <input
                      type="text"
                      value={(templateForm.variables || []).join(', ')}
                      onChange={(e) => {
                        const vars = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                        setTemplateForm({...templateForm, variables: vars});
                      }}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="user_name, email, app_link"
                    />
                    {templateForm.variables && templateForm.variables.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {templateForm.variables.map(v => (
                          <code key={v} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-sm">
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Email Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Your Email Subject"
                    />
                  </div>

                  {/* HTML Body */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      HTML Template <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={templateForm.html_body}
                      onChange={(e) => setTemplateForm({...templateForm, html_body: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                      rows="15"
                      placeholder="<html>...</html>"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Full HTML with styling. Use variables like {`{{user_name}}`}
                    </p>
                  </div>

                  {/* Text Body */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-2">
                      Plain Text Version <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={templateForm.text_body}
                      onChange={(e) => setTemplateForm({...templateForm, text_body: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                      rows="10"
                      placeholder="Plain text version..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fallback for email clients that don't support HTML
                    </p>
                  </div>

                  {/* Preview Button */}
                  <div>
                    <button
                      onClick={() => {
                        // Create preview with sample data
                        let previewHtml = templateForm.html_body;
                        const sampleData = {
                          user_name: 'John Doe',
                          reset_link: 'https://example.com/reset?token=abc123',
                          app_link: 'https://example.com',
                          timestamp: new Date().toLocaleString(),
                          ip_address: '192.168.1.1',
                          device: 'Chrome on Windows',
                          location: 'New York, USA'
                        };
                        
                        // Replace variables
                        Object.keys(sampleData).forEach(key => {
                          const regex = new RegExp(`{{${key}}}`, 'g');
                          previewHtml = previewHtml.replace(regex, sampleData[key]);
                        });
                        
                        // Open preview in new window
                        const previewWindow = window.open('', '_blank');
                        previewWindow.document.write(previewHtml);
                        previewWindow.document.close();
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium mb-4"
                    >
                      👁️ Preview Template
                    </button>
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={saveTemplate}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                    >
                      💾 Save Template
                    </button>
                    <button
                      onClick={cancelEditTemplate}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bulk Emails Tab */}
          {activeTab === 'bulk' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">📬 Send Bulk Emails</h3>
              
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <h4 className="font-bold text-orange-900 dark:text-orange-100 mb-2">⚠️ Bulk Email Marketing</h4>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Send promotional emails to all users or paid members only. Use this feature for discounts, announcements, and special offers.
                </p>
              </div>

              {/* Audience Selection */}
              <div>
                <label className="block text-sm font-medium dark:text-white mb-2">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex-1"
                    style={{
                      borderColor: bulkAudience === 'all' ? '#3b82f6' : '#d1d5db',
                      backgroundColor: bulkAudience === 'all' ? '#eff6ff' : 'transparent'
                    }}
                  >
                    <input
                      type="radio"
                      name="audience"
                      value="all"
                      checked={bulkAudience === 'all'}
                      onChange={(e) => setBulkAudience(e.target.value)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-semibold dark:text-white">👥 All Users</div>
                      <div className="text-xs text-gray-500">Send to everyone (free + paid)</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-2 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex-1"
                    style={{
                      borderColor: bulkAudience === 'paid' ? '#f59e0b' : '#d1d5db',
                      backgroundColor: bulkAudience === 'paid' ? '#fffbeb' : 'transparent'
                    }}
                  >
                    <input
                      type="radio"
                      name="audience"
                      value="paid"
                      checked={bulkAudience === 'paid'}
                      onChange={(e) => setBulkAudience(e.target.value)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-semibold dark:text-white">⭐ Paid Members Only</div>
                      <div className="text-xs text-gray-500">Send to premium subscribers</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium dark:text-white mb-2">
                  Email Template <span className="text-red-500">*</span>
                </label>
                <select
                  value={bulkTemplate}
                  onChange={(e) => setBulkTemplate(e.target.value)}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="discount_coupon">🎁 Marketing - Discount & Coupon</option>
                  <option value="welcome_free">🎉 Welcome Email - FREE</option>
                  <option value="welcome_pro">🎊 Welcome Email - PRO</option>
                  <option value="upgrade_confirmation">🚀 Upgrade Confirmation</option>
                  <option value="subscription_renewal">🔄 Subscription Renewal</option>
                </select>
              </div>

              {/* Dynamic Variables */}
              {bulkTemplate === 'discount_coupon' && (
                <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">📋 Template Variables</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                    Fill in the details for your promotional email:
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Greeting Text</label>
                      <input
                        type="text"
                        value={bulkVariables.greeting_text || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, greeting_text: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Special Offer for"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Discount Percent</label>
                      <input
                        type="number"
                        value={bulkVariables.discount_percent || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, discount_percent: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="50"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Discount Amount</label>
                      <input
                        type="text"
                        value={bulkVariables.discount_amount || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, discount_amount: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="$25"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Coupon Code</label>
                      <input
                        type="text"
                        value={bulkVariables.coupon_code || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, coupon_code: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="DARIJA50"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Expiry Date</label>
                      <input
                        type="date"
                        value={bulkVariables.expiry_date || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, expiry_date: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium dark:text-white mb-1">Redeem Link</label>
                      <input
                        type="url"
                        value={bulkVariables.redeem_link || ''}
                        onChange={(e) => setBulkVariables({...bulkVariables, redeem_link: e.target.value})}
                        className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-1">Email Body Text</label>
                    <textarea
                      value={bulkVariables.email_body || ''}
                      onChange={(e) => setBulkVariables({...bulkVariables, email_body: e.target.value})}
                      className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      rows="3"
                      placeholder="We're excited to offer you an exclusive discount..."
                    />
                  </div>
                </div>
              )}

              {/* Send Button */}
              <div className="flex items-center gap-4">
                <button
                  onClick={sendBulkEmail}
                  disabled={sendingBulk || !bulkTemplate}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:opacity-90 font-bold text-lg disabled:opacity-50 shadow-lg"
                >
                  {sendingBulk ? '📤 Sending Bulk Emails...' : '📬 Send Bulk Email Campaign'}
                </button>
              </div>

              {/* Stats Display */}
              {bulkStats.total > 0 && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <h4 className="font-bold text-green-900 dark:text-green-100 mb-2">📊 Campaign Results</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-700 dark:text-green-300">{bulkStats.total}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Total Recipients</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{bulkStats.sent}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Sent Successfully</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-700 dark:text-red-300">{bulkStats.failed}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                    </div>
                  </div>
                </div>
              )}

              {!providerConfig.api_key && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    ⚠️ Please configure email provider first in the "Email Providers" tab.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold dark:text-white mb-4">Email Notifications</h3>
              
              <div className="space-y-3">
                {[
                  { type: 'welcome', name: 'Welcome Email', desc: 'Sent when new user registers', icon: '🎉' },
                  { type: 'email_verification', name: 'Email Verification', desc: 'Sent to verify user email address', icon: '✅' },
                  { type: 'password_reset', name: 'Password Reset', desc: 'Sent when user requests password reset', icon: '🔐' },
                  { type: 'security_alert', name: 'Security Alert', desc: 'Sent when password is changed', icon: '🔔' },
                  { type: 'login_notification', name: 'Login Notification', desc: 'Sent on new device login', icon: '🔑' },
                  { type: 'holiday_reminder', name: 'Holiday Reminders', desc: 'Sent 7 days before Moroccan holidays', icon: '🎊' },
                  { type: 'subscription_confirmation', name: 'Subscription Confirmation', desc: 'Sent when user upgrades to premium', icon: '💳' },
                  { type: 'achievement_notification', name: 'Achievement Unlocked', desc: 'Sent when user reaches milestones', icon: '🏆' },
                  { type: 'admin_custom', name: 'Admin Custom Email', desc: 'Manually sent by admin to users', icon: '✉️' },
                  { type: 'bulk_email', name: 'Bulk Campaigns', desc: 'Marketing and announcement emails', icon: '📧' }
                ].map(notif => (
                  <div key={notif.type} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium dark:text-white">
                          <span className="mr-2">{notif.icon}</span>
                          {notif.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{notif.desc}</div>
                      </div>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" defaultChecked className="rounded" />
                        <span className="text-sm dark:text-gray-300">Enabled</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold dark:text-white">Email Logs</h3>
                <button
                  onClick={loadEmailLogs}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  🔄 Refresh
                </button>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {emailLogs.length} recent emails
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {emailLogs.map(log => (
                  <div key={log.id} className={`p-3 border rounded ${
                    log.status === 'sent' 
                      ? 'border-green-200 dark:border-green-900' 
                      : 'border-red-200 dark:border-red-900'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium dark:text-white">
                          {log.status === 'sent' ? '✅' : '❌'} {log.subject}
                        </div>
                        <div className="text-sm text-gray-500">
                          To: {log.to_email} • {new Date(log.sent_at).toLocaleString()}
                        </div>
                        {log.error_message && (
                          <div className="text-sm text-red-600 mt-1">Error: {log.error_message}</div>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${
                        log.status === 'sent'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  </div>
                ))}
                {emailLogs.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No emails sent yet</p>
                )}
              </div>
            </div>
          )}

          {/* Test Email Tab */}
          {activeTab === 'test' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white mb-4">🧪 Send Test Email</h3>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Send a test email to verify your configuration is working correctly.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-white mb-2">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="test@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-white mb-2">
                  Email Template
                </label>
                <select
                  value={testTemplate}
                  onChange={(e) => setTestTemplate(e.target.value)}
                  className="w-full px-3 py-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="password_reset">🔐 Password Reset</option>
                  <option value="welcome_free">🎉 Welcome Email - FREE</option>
                  <option value="welcome_pro">🎊 Welcome Email - PRO</option>
                  <option value="upgrade_confirmation">🚀 Upgrade Confirmation</option>
                  <option value="payment_successful">✅ Payment Successful</option>
                  <option value="subscription_renewal">🔄 Subscription Renewal</option>
                  <option value="security_alert">🔔 Security Alert</option>
                  <option value="login_notification">🔐 Login Notification</option>
                  <option value="discount_coupon">🎁 Marketing - Discount & Coupon</option>
                </select>
              </div>

              <button
                onClick={sendTestEmail}
                disabled={sending || !testEmail}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {sending ? 'Sending...' : '📧 Send Test Email'}
              </button>

              {!providerConfig.api_key && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    ⚠️ Please configure email provider first in the "Email Providers" tab.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Template Confirmation Modal */}
      {templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !deleting && setTemplateToDelete(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[fadeIn_0.15s_ease-out]" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
                  🗑️
                </div>
                <div>
                  <h3 className="text-xl font-bold">Delete Template?</h3>
                  <p className="text-sm text-red-100">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You are about to permanently delete this email template:
              </p>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border-2 border-red-200 dark:border-red-800 mb-2">
                <div className="font-bold text-red-900 dark:text-red-100 text-lg break-all">
                  {templateToDelete.name || templateToDelete.template_type}
                </div>
                <div className="text-xs text-red-700 dark:text-red-300 mt-1 font-mono break-all">
                  {templateToDelete.template_type}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                ⚠️ Any code that sends this template type will fall back to the hardcoded default.
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setTemplateToDelete(null)}
                disabled={deleting}
                className="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTemplate}
                disabled={deleting}
                className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg hover:opacity-90 font-semibold shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Deleting…
                  </>
                ) : (
                  <>🗑️ Delete Template</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal */}
      {showSendModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSendModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
                    📧
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Send Test Email</h3>
                    <p className="text-sm text-green-100">Preview before sending to users</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="mb-6">
                <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
                  <div className="text-3xl">✉️</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-1">
                      {selectedTemplate.name}
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {selectedTemplate.description}
                    </p>
                    <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                      Template: <code className="bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">{selectedTemplate.template_type}</code>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 dark:text-green-400">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-200">Sent to your email</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Test email will be sent to your admin account</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 dark:text-blue-400">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-200">Sample data included</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">All variables populated with realistic examples</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-600 dark:text-purple-400">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-200">Marked as test</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Subject line will include [TEST] prefix</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  💡 <strong>Tip:</strong> Check your email inbox to see exactly how users will receive this template!
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex gap-3">
              <button
                onClick={() => setShowSendModal(false)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmSendTestToAdmin}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 font-bold shadow-lg transition-all"
              >
                📧 Send Test Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagementPanel;
